import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { IClient, IMonthPresence } from '../../core/cm.types';
import appService from "../../services/app.service";
import { Select } from '../fields/Select';
import moment from 'moment';
import 'moment/locale/de';
import { Loading } from '../Loading';
import "./PresencePrint.css"
import { TableService } from '../../services/table.service';
import { PresenceAllClients } from '../views/PresenceAllClients';
import graphService from "../../services/graph.service";

export interface IDayDefinition {
    isFreeDay: boolean,
    title: number
}

export const PresencePrint: React.FunctionComponent<{ clients: IClient[], tableService: TableService, exportAllTable(rows), clientDriveId: string, allowanceFactor: number, mailMergeDriveId: string, templateDriveId: string }> = (props) => {
    const currentDate = new Date();

    const { t } = useTranslation();
    const { tableService, clients, exportAllTable, clientDriveId, mailMergeDriveId, templateDriveId, allowanceFactor } = props;
    const [currentMonth, setCurrentMonth] = React.useState<number>(currentDate.getMonth());
    const [currentYear, setCurrentYear] = React.useState<number>(currentDate.getFullYear());
    const [selectedRows, setSelectedRows] = useState([]);
    const [presence, setPresence] = useState<IMonthPresence>({});
    const [currentAmmClients, setCurrentAmmClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [days, setDays] = useState<IDayDefinition[]>([]);

    const fetchData = async () => {
        setIsLoading(true);
        setPresence({})
        setDays([])
        setSelectedRows([])
        setCurrentAmmClients([])

        try {
            var presence = await appService.getPresence(currentYear, currentMonth);
            let addedClients = [];
            presence.details.forEach(detail => {
                const customer = clients.find(a => a.key === detail.customerId);
                if (customer !== undefined) {
                    detail.customerName = customer.firstName + " " + customer.lastName;
                    addedClients.push(customer);
                }
            });

            setPresence(presence);
            setCurrentAmmClients(addedClients)
            setDays(days)
            setSelectedRows([])
        }
        catch {
            setDays([])
            setSelectedRows([])
            setCurrentAmmClients([])
        }
        setIsLoading(false);
    };

    function checkDate(val) {
        if (val === null || val === undefined)
            return "";

        const d = new Date(val);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        let year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return day + "." + month + "." + year;
    }

    function checkValue(val) {
        try {
            if (val === null || val === undefined)
                return "";

            return val;
        } catch (e) {
            return "error";
        }
    }

    const printData = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const selectedFolder = "/Administration/AMMB"
        const alks = await appService.getAlks();;
        const employees = await appService.getEmployees();
        const officeLocations = await appService.getOfficeLocations();
        const terminations = await appService.getTerminations();
        const expenses = await appService.getExpenses();

        await Promise.all(selectedRows.map(async client => {
            try {
                const fileName = `${currentYear}_${moment.months()[currentMonth]}_${new Date().getDate()}_${client?.lastName?.toString()}_${client?.firstName?.toString()}_${client?.personId}.pdf`
                let targetFolder = undefined;
                
                let path = `/root:/${client?.sharePointFolder}${selectedFolder}`;
                let ammPath  = `/root:/${client?.sharePointFolder}${selectedFolder}`;
                try {
                    targetFolder = await graphService.getFolderIdByDrivePath(clientDriveId, path);
                }
                catch {
                    path = `/root:/${client?.sharePointFolder}`;
                    ammPath  = `/root:/${client?.sharePointFolder}`;
                    targetFolder = await graphService.getFolderIdByDrivePath(clientDriveId, path);
                }
                path = `/drives/${clientDriveId}/items${path}`;
                ammPath = `/drives/${clientDriveId}/items${ammPath}`;

                path += `/${fileName}`;
                
                const myAlk = alks.find(a => a.key === client?.alkId);

                const termination = terminations.find(r => r.key === client?.amm?.terminationId)
                const employeeAMM = employees.find(e => e.key === client?.amm?.administeredFrom);
                const employeeAMMLocation = officeLocations.find(e => e.key === employeeAMM?.officeLocationId);

                const userPresence = presence.details.find(p => p.customerId === client.key);
                const calculateTransferExpenses = function () {
                    let entries = 0;
                    for (var i = 1; i <= 31; i++) {
                        const xVM = `v${i}`;
                        const xNM = `n${i}`;
                        const actualVM = userPresence[xVM] !== undefined ? userPresence[xVM].toLowerCase() : "";
                        const actualNM = userPresence[xNM] !== undefined ? userPresence[xNM].toLowerCase() : "";

                        const currentMoney = 'x' === actualVM || 'x' === actualNM;
                        if (currentMoney) {
                            entries += 1;
                        }
                    }
                    return entries;
                };
                const calculateRationDays = function () {
                    let rationMoneyDays = 0;
                    for (var i = 1; i <= 31; i++) {
                        const xVM = `v${i}`;
                        const xNM = `n${i}`;
                        const actualVM = userPresence[xVM] !== undefined ? userPresence[xVM].toLowerCase() : "";
                        const actualNM = userPresence[xNM] !== undefined ? userPresence[xNM].toLowerCase() : "";

                        const currentMoney = expenses.find((expense) => (expense.vm !== undefined ? expense.vm.toLocaleLowerCase() : "") === actualVM && (expense.nm !== undefined ? expense.nm.toLocaleLowerCase() : "") === actualNM);
                        if (currentMoney !== undefined) {
                            rationMoneyDays = currentMoney.isMealExpense ? rationMoneyDays + 1 : rationMoneyDays;
                        }
                    }
                    return rationMoneyDays;
                };
                const rationMoneyDays = calculateRationDays();
                const transferDays = calculateTransferExpenses();

                const alkAddress = `${checkValue(myAlk?.title)}\n${checkValue(myAlk?.street)}\n${checkValue(myAlk?.zip)} ${checkValue(myAlk?.city)}\nTelefon: ${checkValue(myAlk?.phone)}\nKassen-Nummer: ${checkValue(myAlk?.alkNumber)}`

                let metadata = {
                    "tn_nachname": checkValue(client?.lastName),
                    "tn_pensum_in_prozent": checkValue(client?.pensum),
                    "tn_personennummer": checkValue(client?.personId),
                    "tn_sozialversicherungsnummer": checkValue(client?.socialSecurityNumber),
                    "tn_vorname": checkValue(client?.firstName),

                    "amm_termination": checkValue(client?.amm?.terminationId),
                    "amm_terminationdate": checkDate(client?.amm?.ammTermination),
                    "amm_transferdays": checkValue(transferDays),
                    "amm_remarks2": checkValue(client?.amm?.ammRemarks),
                    "amm_flatrate": checkValue(client?.amm?.flatrate),
                    "amm_useFlatrate": checkValue(client?.amm?.useFlatrate),
                    "amm_transfercosts": checkValue(client?.amm?.transferCosts),
                    "amm_noParticipation": checkValue(client?.amm?.noParticipation),
                    "amm_correction": checkValue(client?.amm?.correction),
                    "tn_entscheidNr": checkValue(client?.amm?.number),
                    "amm_remarks": checkValue(client?.amm?.remarks),
                    "amm_cancelbyorganisation": termination?.title === 'Anbieter',
                    "amm_cancelbymutual": termination?.title === 'Gegenseitig',
                    "amm_cancelbyinsurant": termination?.title === 'Versicherten',
                    "amm_cancelbyrav": termination?.title === 'RAV',
                    "tn_alk_name": checkValue(myAlk?.title),
                    "tn_alk_address": checkValue(alkAddress),
                    "tn_alk_info_mail": checkValue(myAlk?.email),
                    "ma_amm": checkValue(
                        employeeAMM === null || employeeAMM === undefined
                            ? ""
                            : employeeAMM.name + " " + employeeAMM.lastName
                    ),
                    "ma_amm_name": checkValue(employeeAMM?.lastName),
                    "ma_amm_vorname": checkValue(employeeAMM?.name),
                    "ma_amm_mail_intern": checkValue(employeeAMM?.email),
                    "ma_amm_tel_intern": checkValue(employeeAMM?.phone),

                    "ma_amm_org_name": checkValue(employeeAMMLocation?.title),
                    "ma_amm_org_strasse": checkValue(employeeAMMLocation?.street),
                    "ma_amm_org_ort": checkValue(employeeAMMLocation?.city),
                    "ma_amm_org_durNummer": checkValue(employeeAMMLocation?.durNummer),
                    "ma_amm_org_uidNummer": checkValue(employeeAMMLocation?.uidNummer),
                    "ma_amm_org_betriebskantine": employeeAMMLocation?.betriebskantine === true,

                    "tn_austritt": checkDate(client?.amm?.assignmentExitDate ?? client?.exit?.date),
                    "tn_eintritt": checkDate(client?.entry?.date),
                    "tn_geburtsdatum": checkDate(client?.birthDay),

                    "amm_rationdays": rationMoneyDays,
                    "amm_rationcost": allowanceFactor,
                    "amm_rationtotal": rationMoneyDays * allowanceFactor,

                    "amm_date": `${String(parseInt(currentMonth.toString()) + 1).padStart(2, "0")}${currentYear.toString()}`,

                    "amm_01vm": checkValue(userPresence?.v1),
                    "amm_02vm": checkValue(userPresence?.v2),
                    "amm_03vm": checkValue(userPresence?.v3),
                    "amm_04vm": checkValue(userPresence?.v4),
                    "amm_05vm": checkValue(userPresence?.v5),
                    "amm_06vm": checkValue(userPresence?.v6),
                    "amm_07vm": checkValue(userPresence?.v7),
                    "amm_08vm": checkValue(userPresence?.v8),
                    "amm_09vm": checkValue(userPresence?.v9),
                    "amm_10vm": checkValue(userPresence?.v10),
                    "amm_11vm": checkValue(userPresence?.v11),
                    "amm_12vm": checkValue(userPresence?.v12),
                    "amm_13vm": checkValue(userPresence?.v13),
                    "amm_14vm": checkValue(userPresence?.v14),
                    "amm_15vm": checkValue(userPresence?.v15),
                    "amm_16vm": checkValue(userPresence?.v16),
                    "amm_17vm": checkValue(userPresence?.v17),
                    "amm_18vm": checkValue(userPresence?.v18),
                    "amm_19vm": checkValue(userPresence?.v19),
                    "amm_20vm": checkValue(userPresence?.v20),
                    "amm_21vm": checkValue(userPresence?.v21),
                    "amm_22vm": checkValue(userPresence?.v22),
                    "amm_23vm": checkValue(userPresence?.v23),
                    "amm_24vm": checkValue(userPresence?.v24),
                    "amm_25vm": checkValue(userPresence?.v25),
                    "amm_26vm": checkValue(userPresence?.v26),
                    "amm_27vm": checkValue(userPresence?.v27),
                    "amm_28vm": checkValue(userPresence?.v28),
                    "amm_29vm": checkValue(userPresence?.v29),
                    "amm_30vm": checkValue(userPresence?.v30),
                    "amm_31vm": checkValue(userPresence?.v31),

                    "amm_01nm": checkValue(userPresence?.n1),
                    "amm_02nm": checkValue(userPresence?.n2),
                    "amm_03nm": checkValue(userPresence?.n3),
                    "amm_04nm": checkValue(userPresence?.n4),
                    "amm_05nm": checkValue(userPresence?.n5),
                    "amm_06nm": checkValue(userPresence?.n6),
                    "amm_07nm": checkValue(userPresence?.n7),
                    "amm_08nm": checkValue(userPresence?.n8),
                    "amm_09nm": checkValue(userPresence?.n9),
                    "amm_10nm": checkValue(userPresence?.n10),
                    "amm_11nm": checkValue(userPresence?.n11),
                    "amm_12nm": checkValue(userPresence?.n12),
                    "amm_13nm": checkValue(userPresence?.n13),
                    "amm_14nm": checkValue(userPresence?.n14),
                    "amm_15nm": checkValue(userPresence?.n15),
                    "amm_16nm": checkValue(userPresence?.n16),
                    "amm_17nm": checkValue(userPresence?.n17),
                    "amm_18nm": checkValue(userPresence?.n18),
                    "amm_19nm": checkValue(userPresence?.n19),
                    "amm_20nm": checkValue(userPresence?.n20),
                    "amm_21nm": checkValue(userPresence?.n21),
                    "amm_22nm": checkValue(userPresence?.n22),
                    "amm_23nm": checkValue(userPresence?.n23),
                    "amm_24nm": checkValue(userPresence?.n24),
                    "amm_25nm": checkValue(userPresence?.n25),
                    "amm_26nm": checkValue(userPresence?.n26),
                    "amm_27nm": checkValue(userPresence?.n27),
                    "amm_28nm": checkValue(userPresence?.n28),
                    "amm_29nm": checkValue(userPresence?.n29),
                    "amm_30nm": checkValue(userPresence?.n30),
                    "amm_31nm": checkValue(userPresence?.n31)
                };

                const bytes = await appService.generateAmm(metadata);

                const ammName = `${currentYear}_${moment.months()[currentMonth]}_${new Date().getDate()}_${client?.lastName?.toString()}_${client?.firstName?.toString()}_${client?.personId}.pdf`;

                const result = await graphService.uploadFileToPath(ammPath, ammName, bytes);
                
                const targetCopyFilePath = `/drives/${mailMergeDriveId}/:root:/`;
                await graphService.copyTemplateToFolderByTargetPath(
                    targetFolder.parentReference.driveId,
                    result.id,
                    targetCopyFilePath,
                    fileName
                    );
            }
            catch (ex) {
                console.log(ex);
                alert(t("client:tabDocuments:failedPrefix") + ` ${client?.firstName} ${client?.lastName} ` + t("client:tabDocuments:failedSuffix"))
            }
        }))
        alert(t("client:tabDocuments:printDone"))
        setIsLoading(false);
    }

    React.useEffect(() => {
        fetchData()
    }, [currentYear, currentMonth, clients]);

    let years = [];
    for (let currentYear = 2018; currentYear <= currentDate.getFullYear() + 1; currentYear++)
        years.push(currentYear);
    const yearsOption = [...years.map((item) => {
        return {
            key: item,
            text: item
        };
    })];

    moment.locale('de');
    const monthNames = moment.months()
    let monthOptions = [...monthNames.map((month, index) => {
        return {
            key: index,
            text: month
        };
    })]

    if (isLoading === true || clients.length === 0)
        return <Loading isLoading={true}></Loading>

    return (
        <div className="section defaultSection">
            <div className="columns">
                <div className="column">
                    <h1 className="title is-3">{t("client:tabPresencePrint:getAMM")}</h1>
                </div>
            </div>
            <div className="field is-horizontal">
                <Select options={yearsOption} value={currentYear} onChange={(e) => setCurrentYear(e.target.value)}></Select>
                <Select options={monthOptions} value={currentMonth} onChange={(e) => setCurrentMonth(e.target.value)}></Select>
            </div>
            <button
                type="button"
                disabled={selectedRows.length === 0}
                className={`button is-link is-small is-fullwidth`}
                onClick={printData}
            >
                {t("client:tabPresencePrint:print")}
            </button >
            <PresenceAllClients clients={currentAmmClients} tableService={tableService} selectedRows={selectedRows} setSelectedRows={setSelectedRows} exportAllTable={exportAllTable} />
            <button
                type="button"
                disabled={selectedRows.length === 0}
                className={`button is-link is-small is-fullwidth`}
                onClick={printData}
            >
                {t("client:tabPresencePrint:print")}
            </button>
        </div >
    );
};
